import React, { useEffect, useState } from 'react';

interface EnvioMeta {
    chainId: number;
    endBlock: number | null;
    sourceBlock: number;
    startBlock: number;
    progressBlock: number;
    readyAt: string;
}

interface EnvioData {
    _meta: EnvioMeta[];
}

interface EnvioCardProps {
    name: string;
    url: string;
    refreshInterval?: number;
    useProxy?: boolean;
    proxyUrl?: string;
}

const formatDate = (dateString: string): string => {
    try {
        const date = new Date(dateString);
        return date.toLocaleString('de-DE', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    } catch {
        return dateString;
    }
};

const formatChainName = (chainId: number): string => {
    const chainNames: Record<number, string> = {
        1: 'Ethereum Mainnet',
        137: 'Polygon',
        42161: 'Arbitrum',
        10: 'Optimism',
        8453: 'Base',
        146: 'Sonic',
        56: 'BSC',
        43114: 'Avalanche'
    };
    return chainNames[chainId] || `Chain ${chainId}`;
};

const calculateProgress = (startBlock: number, progressBlock: number, endBlock: number | null): number => {
    if (endBlock === null) {
        // If no end block, we can't calculate progress percentage
        return 0;
    }
    if (endBlock === startBlock) return 100;
    return ((progressBlock - startBlock) / (endBlock - startBlock)) * 100;
};

const EnvioCard: React.FC<EnvioCardProps> = ({ 
    name,
    url,
    refreshInterval = 5000,
    useProxy = false,
    proxyUrl = 'https://api.allorigins.win/raw?url='
}) => {
    const [data, setData] = useState<EnvioData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    const fetchEnvioData = async () => {
        try {
            const fetchUrl = useProxy ? `${proxyUrl}${encodeURIComponent(url)}` : url;
            const response = await fetch(fetchUrl, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
                mode: 'cors'
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const jsonData: EnvioData = await response.json();
            setData(jsonData);
            setError(null);
            setLoading(false);
        } catch (err) {
            console.error(`Error fetching Envio data from ${url}:`, err);
            setError('Failed to fetch Envio data');
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEnvioData();
        const intervalId = setInterval(fetchEnvioData, refreshInterval);

        return () => {
            clearInterval(intervalId);
        };
    }, [url, refreshInterval]);

    return (
        <div className="envio-card" style={{
            backgroundColor: 'var(--metro-card-bg)',
            border: '1px solid var(--metro-card-border)',
            borderRadius: 'var(--card-radius)',
            padding: '20px',
            marginBottom: '24px'
        }}>
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '20px' 
            }}>
                <h3 style={{ margin: 0, color: 'var(--metro-text)' }}>
                    {name}
                </h3>
                <div style={{ 
                    color: 'var(--metro-text-secondary)',
                    fontSize: '14px'
                }}>
                    {loading ? 'Loading...' : error ? 'Error' : 'Online'}
                </div>
            </div>
            
            {error ? (
                <div style={{ color: 'var(--metro-error)' }}>{error}</div>
            ) : loading ? (
                <div style={{ color: 'var(--metro-text-secondary)' }}>Loading data...</div>
            ) : data && data._meta && data._meta.length > 0 ? (
                <div style={{ display: 'grid', gap: '16px' }}>
                    {data._meta.map((meta, index) => {
                        const progress = calculateProgress(meta.startBlock, meta.progressBlock, meta.endBlock);
                        const totalBlocks = meta.endBlock ? meta.endBlock - meta.startBlock : null;
                        const processedBlocks = meta.progressBlock - meta.startBlock;
                        const remainingBlocks = meta.endBlock ? meta.endBlock - meta.progressBlock : null;

                        return (
                            <div key={index} style={{
                                backgroundColor: 'var(--metro-dark-secondary)',
                                borderRadius: '8px',
                                padding: '16px'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '12px'
                                }}>
                                    <h4 style={{ 
                                        margin: 0, 
                                        color: 'var(--metro-text)',
                                        fontSize: '16px'
                                    }}>
                                        {formatChainName(meta.chainId)} (Chain ID: {meta.chainId})
                                    </h4>
                                    <div style={{
                                        fontSize: '12px',
                                        color: 'var(--metro-text-secondary)'
                                    }}>
                                        Ready: {formatDate(meta.readyAt)}
                                    </div>
                                </div>
                                
                                <div style={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                                    gap: '12px',
                                    marginBottom: '12px',
                                    fontSize: '14px'
                                }}>
                                    <div>
                                        <div style={{ color: 'var(--metro-text-secondary)', fontSize: '12px' }}>Start Block</div>
                                        <div style={{ color: 'var(--metro-text)', fontWeight: 500 }}>
                                            {meta.startBlock.toLocaleString()}
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ color: 'var(--metro-text-secondary)', fontSize: '12px' }}>Progress Block</div>
                                        <div style={{ color: 'var(--metro-text)', fontWeight: 500 }}>
                                            {meta.progressBlock.toLocaleString()}
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ color: 'var(--metro-text-secondary)', fontSize: '12px' }}>Source Block</div>
                                        <div style={{ color: 'var(--metro-text)', fontWeight: 500 }}>
                                            {meta.sourceBlock.toLocaleString()}
                                        </div>
                                    </div>
                                    {meta.endBlock !== null && (
                                        <div>
                                            <div style={{ color: 'var(--metro-text-secondary)', fontSize: '12px' }}>End Block</div>
                                            <div style={{ color: 'var(--metro-text)', fontWeight: 500 }}>
                                                {meta.endBlock.toLocaleString()}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {meta.endBlock !== null && (
                                    <>
                                        <div style={{ marginBottom: '8px' }}>
                                            <div style={{ 
                                                display: 'flex', 
                                                justifyContent: 'space-between',
                                                marginBottom: '4px',
                                                fontSize: '12px',
                                                color: 'var(--metro-text-secondary)'
                                            }}>
                                                <span>Progress: {progress.toFixed(2)}%</span>
                                                <span>
                                                    {processedBlocks.toLocaleString()} / {totalBlocks?.toLocaleString()} blocks
                                                    {remainingBlocks !== null && ` (${remainingBlocks.toLocaleString()} remaining)`}
                                                </span>
                                            </div>
                                            <div className="progress-container" style={{
                                                width: '100%',
                                                height: '8px',
                                                backgroundColor: 'var(--metro-dark-secondary)',
                                                borderRadius: '4px',
                                                overflow: 'hidden'
                                            }}>
                                                <div 
                                                    className={`progress-bar ${progress < 99.9 ? 'warning' : ''}`}
                                                    style={{ 
                                                        width: `${progress}%`,
                                                        height: '100%',
                                                        backgroundColor: progress >= 99.9 ? 'var(--metro-success)' : 'var(--metro-accent)',
                                                        transition: 'width 0.3s ease'
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}

                                {meta.endBlock === null && (
                                    <div style={{
                                        fontSize: '12px',
                                        color: 'var(--metro-text-secondary)',
                                        fontStyle: 'italic'
                                    }}>
                                        No end block specified - indexing in progress
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div style={{ color: 'var(--metro-text-secondary)' }}>No data available</div>
            )}
        </div>
    );
};

export default EnvioCard;

